import React, { useEffect, useState } from 'react'
import { Box, Flex, Button } from 'rebass'
import { postBanner, updateBanner } from '../../../store/actions/banners'
import {
  BannerDataProps,
  GETBannerDataProps,
} from '../../../interfaces/banners'
import _ from 'lodash'
import { AppState } from '../../../store/rootReducer'
import { AuthStateProps } from '../../../interfaces/auth'
import { DropDownObjectProps } from '../../../interfaces/interfaces'
import { setSnackBarData } from '../../../store/actions/ui'
import { useDispatch, useSelector } from 'react-redux'
import {
  localInputToUtcIsoString,
  toDateTimeLocalValue,
  utcToLocalDateTimeInputValue,
} from '../../../utils/dateTime'

type CreateBannerProps = {
  onClose: () => void
  refreshBanners?: () => Promise<void> | void
  initialBanner?: GETBannerDataProps
}

type OrgModel = {
  level: string
  list: DropDownObjectProps[]
  orgObj?: DropDownObjectProps
}

export default function CreateBanner({
  onClose,
  refreshBanners,
  initialBanner,
}: CreateBannerProps) {
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [bannerType, setBannerType] = useState<number>(
    initialBanner?.bannerType ?? 99
  )
  const [bannerSeverity, setSeverity] = useState<number>(
    initialBanner?.bannerSeverity ?? 99
  )
  const [startTime, setStartTime] = React.useState(
    utcToLocalDateTimeInputValue(initialBanner?.startDate)
  )
  const [endTime, setEndTime] = React.useState(
    utcToLocalDateTimeInputValue(initialBanner?.endDate)
  )
  const [message, setMessage] = React.useState(initialBanner?.contents ?? '')
  const [header, setHeader] = React.useState(initialBanner?.header ?? '')
  const maxAdd = 10

  const { authorizedOrganizations, options, token, hca34 } = useSelector<
    AppState,
    Partial<AuthStateProps>
  >(({ auth: { authorizedOrganizations, options, token, hca34 } }) => ({
    authorizedOrganizations,
    options,
    token,
    hca34,
  }))

  const organizationLevels: DropDownObjectProps[] = _.uniqBy(
    authorizedOrganizations?.map((auth) => ({
      text: auth?.organization?.typeName || '',
      value: auth?.organization?.typeName || '',
      id: auth?.organization?.typeId || '',
    })) || [],
    'text'
  )

  const [organization, setMultiOrg] = useState<OrgModel[]>([
    { level: '', list: [], orgObj: undefined },
  ])

  const isMounted = React.useRef(true)
  React.useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const isEditMode = Boolean(initialBanner?.id)

  {
    /* Update organization list when level changes automatically */
  }
  useEffect(() => {
    if (initialBanner?.scope && options) {
      const scopeIds = initialBanner.scope
        .split('|')
        .map((item) => item.trim())
        .filter(Boolean)

      const prefilledOrganizations: OrgModel[] = []

      scopeIds.forEach((scopeId) => {
        const matchedEntry = Object.entries(options).find(([, list]) =>
          (list || []).some((option) => option.id === scopeId)
        )

        if (!matchedEntry) {
          return
        }

        const [level, list = []] = matchedEntry
        const orgObj = list.find((option) => option.id === scopeId)

        if (!orgObj) {
          return
        }

        prefilledOrganizations.push({ level, list, orgObj })
      })

      if (prefilledOrganizations.length > 0) {
        setMultiOrg(prefilledOrganizations)
        return
      }
    }

    if (!options) return
    setMultiOrg((prev) =>
      prev.map((pair) => {
        const newList = pair.level ? options[pair.level] || [] : []
        return { ...pair, list: newList, orgObj: newList[0] }
      })
    )
  }, [initialBanner, options])

  const handleLevelChange = (index: number, level: string) => {
    const newList = options?.[level] || []
    setMultiOrg((prev) =>
      prev.map((p, i) =>
        i === index ? { level, list: newList, orgObj: newList[0] } : p
      )
    )
  }

  const handleOrgChange = (index: number, orgText: string) => {
    setMultiOrg((prev) =>
      prev.map((p, i) =>
        i === index
          ? { ...p, orgObj: p.list.find((o) => o.text === orgText) }
          : p
      )
    )
  }

  const addNewOrg = () => {
    if (organization.length >= maxAdd) return
    setMultiOrg((prev) => [...prev, { level: '', list: [], orgObj: undefined }])
  }

  const deleteOrg = (indexToDelete: number) => {
    setMultiOrg((prev) => prev.filter((_, i) => i !== indexToDelete))
  }

  const now = new Date()
  const nowLocalInput = toDateTimeLocalValue(now)
  const start = startTime ? new Date(startTime) : null
  const end = endTime ? new Date(endTime) : null

  const isDisabled: boolean =
    !organization.every((p) => p.level && p.orgObj) ||
    !startTime ||
    !endTime ||
    !header ||
    !message ||
    Number(bannerType) === 99 ||
    Number(bannerSeverity) === 99 ||
    (start !== null && start < now) ||
    (start !== null && end !== null && end.getTime() <= start.getTime())

  const handleSave = async () => {
    console.log('Save button pressed')
    if (isDisabled || isSubmitting) {
      console.warn('Missing required fields')
      return
    }
    setIsSubmitting(true)
    const formartForPost: BannerDataProps & {
      scope: string
      contents: string
      endDate: string
      startDate: string
      createdBy34: string
      header: string
      id?: string
    } = {
      scope: organization.map((p) => p.orgObj?.id || '').join(' | '),
      startDate: localInputToUtcIsoString(startTime),
      endDate: localInputToUtcIsoString(endTime),
      bannerType: bannerType as unknown as BannerDataProps['bannerType'],
      bannerSeverity:
        bannerSeverity as unknown as BannerDataProps['bannerSeverity'],
      contents: message,
      createdBy34: hca34 ?? '',
      header: header,
      ...(isEditMode && initialBanner?.id ? { id: initialBanner.id } : {}),
    }

    const success = isEditMode
      ? await dispatch<any>(
          updateBanner({
            token: token || '',
            banner: {
              ...formartForPost,
              id: initialBanner?.id,
              bannerStatus: initialBanner?.bannerStatus,
            },
          })
        )
      : await dispatch<any>(
          postBanner({ token: token || '', banners: formartForPost })
        )

    try {
      if (success) {
        await refreshBanners?.()
        onClose()
      } else {
        dispatch(
          setSnackBarData({ type: 'error', message: 'Something went wrong' })
        )
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false)
      }
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bg: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          bg: 'white',
          borderRadius: 12,
          width: ['90%', '650px'],
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 4, pb: 0, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <h2 style={{ textAlign: 'center' }}>
            {isEditMode ? 'Edit Banner' : 'Create New Banner'}
          </h2>

          {organization.map((pair, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  mb: 3,
                  alignItems: 'center',
                }}
              >
                {/* Organization Level */}
                <Box flex={1}>
                  <label>Organization Level</label>
                  <select
                    value={pair.level}
                    onChange={(e) => handleLevelChange(index, e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                    disabled={isSubmitting}
                  >
                    <option value="">---</option>
                    {organizationLevels.map((level) => (
                      <option key={level.id} value={level.text}>
                        {level.text}
                      </option>
                    ))}
                  </select>
                </Box>

                {/* Organization Name */}
                <Box flex={1}>
                  <label>Organization Name</label>
                  <select
                    value={pair.orgObj?.text || ''}
                    onChange={(e) => handleOrgChange(index, e.target.value)}
                    style={{ width: '100%', padding: '8px' }}
                    disabled={!pair.list.length || isSubmitting}
                  >
                    {pair.list.length === 0 ? (
                      <option>---</option>
                    ) : (
                      pair.list.map((org) => (
                        <option key={org.id} value={org.text}>
                          {org.text}
                        </option>
                      ))
                    )}
                  </select>
                </Box>

                {/* Delete button */}
                {organization.length > 1 && (
                  <Box>
                    <button
                      onClick={() => deleteOrg(index)}
                      disabled={isSubmitting}
                      style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '18px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: '#ce2130',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignSelf: 'center',
                        transform: 'translateY(10px)',
                      }}
                    >
                      <span
                        style={{
                          display: 'block',
                          transform: 'translateY(-1px)',
                        }}
                      >
                        x
                      </span>
                    </button>
                  </Box>
                )}
              </Box>
            </React.Fragment>
          ))}

          {/* Add New */}
          <Button
            color={organization.length >= maxAdd ? '#bcbcbc' : '#005589'}
            variant="outline"
            onClick={addNewOrg}
            disabled={organization.length >= maxAdd || isSubmitting}
            sx={{
              mb: 3,
              cursor: 'pointer',
              fontSize: '12px',
              px: 2,
              py: 1,
            }}
          >
            + Add New
          </Button>

          {/* Title */}
          <Box mb={3}>
            <label>Title</label>
            <textarea
              value={header}
              onChange={(e) => setHeader(e.target.value)}
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '6px',
                height: '35px',
              }}
            />
          </Box>

          {/* Banner Type */}
          <Box mb={3}>
            <label>Type of Banner</label>
            <select
              value={bannerType}
              onChange={(e) => setBannerType(Number(e.target.value))}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value={99}>---</option>
              <option value={0}>Static</option>
              <option value={1}>Dismissable</option>
            </select>
          </Box>

          {/* Banner Type */}
          <Box mb={3}>
            <label>Banner Severity</label>
            <select
              value={bannerSeverity}
              onChange={(e) => setSeverity(Number(e.target.value))}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value={99}>---</option>
              <option value={0}>Information</option>
              <option value={1}>Incidental</option>
            </select>
          </Box>

          {/* Start Date */}
          <Box mb={3}>
            <label>Start Date & Time</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
              min={nowLocalInput}
            />
          </Box>

          {/* End Date */}
          <Box mb={3}>
            <label>End Date & Time</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px' }}
              min={startTime || nowLocalInput}
            />
          </Box>

          {/* Message */}
          <Box mb={3}>
            <label>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isSubmitting}
              style={{ width: '100%', padding: '8px', minHeight: '80px' }}
              maxLength={120}
            />
            <label style={{ display: 'block', fontSize: '10px' }}>
              {message.length} / 120 characters
            </label>
          </Box>
        </Box>

        {/* Save & Cancel Options */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bg: 'white',
            px: 4,
            py: 3,
            borderTop: '1px solid #e5e5e5',
            zIndex: 1,
          }}
        >
          <Flex justifyContent="flex-end">
            <Button
              bg="gray"
              color="white"
              mr={2}
              onClick={onClose}
              disabled={isSubmitting}
              sx={{
                cursor: 'pointer',
              }}
            >
              Cancel
            </Button>
            <Button
              bg={isDisabled ? '#bcbcbc' : '#005589'}
              color="white"
              onClick={handleSave}
              disabled={isDisabled || isSubmitting}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                cursor: isDisabled || isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting && (
                <Box
                  as="span"
                  sx={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.45)',
                    borderTopColor: '#fff',
                    borderRadius: '50%',
                    animation: 'banner-spin 0.8s linear infinite',
                  }}
                />
              )}
              {isSubmitting
                ? isEditMode
                  ? 'Saving...'
                  : 'Submitting...'
                : isEditMode
                ? 'Save'
                : 'Submit'}
            </Button>
          </Flex>
        </Box>
        <style>
          {`@keyframes banner-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </Box>
    </Box>
  )
}
